import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { TabsMenu } from '../../components';
import { tabsMenuOptions } from '../../utils/helpers/reports/helperRevenueReport';
import { WeekNavigator } from '../../components';
import { fetchViolations } from '../../redux/violationsReport/violationsThunks';
import {
  selectIsDrawerOpen,
  selectSelectedViolation,
  selectSelectedWeek,
  setCleanViolation,
  setSelectedWeek,
} from '../../redux/violationsReport/violationsReportSlice';
import { selectSelectedReport, setSelectedReport } from '../../redux/reports/reportsSlice';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';
import { ReportsMainContainer, TopContainter, MainContentReports, TableContent } from './Reports.styles';
import AdminDrawer from '../../components/AdminDrawer/AdminDrawer';
import { selectShiftStatus } from '../../redux/shifts/shiftsSlice';
import { ViolationDetails } from '../../components/ViolationsReports';

import { Box } from '@mui/material';
import ReportContent from '../../components/Reports/ReportContent/ReportContent';
import { LOADING_STATES } from '../../utils/constants/loadingStates';

const Reports = () => {
  const dispatch = useDispatch();

  // Selectors
  const selectedWeek = useSelector(selectSelectedWeek);
  const selectedViolation = useSelector(selectSelectedViolation);
  const isDrawerOpen = useSelector(selectIsDrawerOpen);
  const selectedReport = useSelector(selectSelectedReport);
  const isLoadingShifts = useSelector(selectShiftStatus) === LOADING_STATES.LOADING;
  const { id: venueID, startOfWeekIndex } = useSelector(selectCurrentVenue);

  // Helper function to get adjusted week range
  const getAdjustedWeekRange = useCallback(
    (date) => {
      const currentDay = dayjs(date).day();
      const daysToSubtract = (currentDay - startOfWeekIndex + 7) % 7;
      const weekStart = dayjs(date).subtract(daysToSubtract, 'day').startOf('day');
      const weekEnd = weekStart.add(6, 'day').endOf('day');

      return {
        start: weekStart.toISOString(),
        end: weekEnd.toISOString(),
      };
    },
    [startOfWeekIndex]
  );

  const defaultWeek = useMemo(() => getAdjustedWeekRange(dayjs()), [getAdjustedWeekRange]);

  // Determine if the selected violation is a break violation
  const isBreakViolation = useMemo(() => (selectedViolation?.details?.break ? true : false), [selectedViolation]);

  // UseEffect to Load the default week
  useEffect(() => {
    if (!selectedWeek) {
      dispatch(setSelectedWeek(defaultWeek));
      dispatch(fetchViolations(venueID));
    }
  }, [selectedWeek, defaultWeek, dispatch, venueID]);

  // Update the week change handler
  const handleWeekChange = useCallback(
    (newDate) => {
      const newWeek = getAdjustedWeekRange(newDate);
      dispatch(setSelectedWeek(newWeek));
      dispatch(fetchViolations(venueID));
    },
    [venueID, dispatch, getAdjustedWeekRange]
  );

  const handleTabChange = (newValue) => {
    dispatch(setSelectedReport(newValue));
  };

  const handleCloseDrawer = useCallback(() => {
    dispatch(setCleanViolation());
  }, [dispatch]);

  const drawerContent = useMemo(() => {
    if (!isDrawerOpen) return null;

    const formattedDate = dayjs(selectedViolation?.timestamp).format('ddd, MMM DD');
    return (
      <Box>
        <AdminDrawer isOpen={isDrawerOpen} title={formattedDate} onClose={handleCloseDrawer}>
          <ViolationDetails
            selectedViolation={selectedViolation}
            isBreakViolation={isBreakViolation}
            isLoading={isLoadingShifts}
          />
        </AdminDrawer>
      </Box>
    );
  }, [isDrawerOpen, handleCloseDrawer, selectedViolation, isBreakViolation, isLoadingShifts]);

  return (
    <ReportsMainContainer>
      <TableContent>
        <TopContainter>
          <WeekNavigator
            width="320px"
            initialDate={defaultWeek.start}
            selectedDate={selectedWeek ? selectedWeek.start : null}
            onWeekChange={handleWeekChange}
          />
          <TabsMenu options={tabsMenuOptions} value={selectedReport} onChange={handleTabChange} />
        </TopContainter>

        <MainContentReports>
          <ReportContent selectedReport={selectedReport} />
        </MainContentReports>
      </TableContent>

      {drawerContent}
    </ReportsMainContainer>
  );
};

export default React.memo(Reports);
